import { useEffect, useState } from "react";
import { ArrowRight, CalendarDays, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateLong, fmtTime, isAbroad, localTzLabel, TZ, userTz, ymdIn, ymdLong, ymdToIso } from "@/lib/format";

function DayShift({ iso, date }) {
  const local = ymdIn(iso, userTz);
  if (local === date) return null;
  return <span className="block text-[10px] font-medium opacity-70">{local < date ? "Previous day" : "Next day"}</span>;
}

export function TimeSlots({ date, selected, onSelect, refreshKey, onContinue }) {
  const [slots, setSlots] = useState(null);

  useEffect(() => {
    if (!date) {
      return;
    }
    let on = true;
    setSlots(null);
    api.get("/availability/slots", { params: { date } })
      .then((r) => on && setSlots(r.data.slots))
      .catch(() => on && setSlots([]));
    return () => {
      on = false;
    };
  }, [date, refreshKey]);

  if (!date) {
    return (
      <div className="h-full min-h-[240px] flex flex-col items-center justify-center text-center px-6" data-testid="slots-empty-prompt">
        <CalendarDays size={28} strokeWidth={1.25} className="text-[#C7A76C]" />
        <p className="mt-4 font-display text-lg font-semibold text-[#172033]">Select a date</p>
        <p className="mt-1 text-sm text-[#697386]">Available times will appear here.</p>
      </div>
    );
  }

  return (
    <div data-testid="time-slots-panel">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#697386]">Available times</p>
      <h3 className="font-display mt-2 text-xl font-semibold text-[#172033]" data-testid="slots-date-label">{ymdLong(date)}</h3>
      <p className="mt-1 text-xs text-[#697386]" data-testid="slots-timezone-label">{localTzLabel(slots?.[0]?.start_utc || ymdToIso(date))}</p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2 max-h-[340px] overflow-y-auto pr-1" role="listbox" aria-label="Available times">
        {slots === null && Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-12 rounded-2xl bg-[#0B1F3A]/5 animate-pulse" />)}
        {slots?.map((s) => {
          const isSel = selected?.start_utc === s.start_utc;
          return (
            <button
              key={s.start_utc}
              disabled={!s.available}
              onClick={() => onSelect(s)}
              role="option"
              aria-selected={isSel}
              data-testid={`time-slot-${s.label.replace(/[: ]/g, "-")}`}
              className={`min-h-12 py-2 rounded-2xl text-sm font-semibold transition-[transform,background-color,border-color] duration-200 ${isSel ? "bg-[#0B1F3A] text-white shadow-lg" : s.available ? "bg-white border border-[rgba(20,35,55,0.10)] text-[#0B1F3A] hover:border-[#0B1F3A]/50 active:scale-[0.98]" : "text-[#697386]/50 line-through cursor-not-allowed border border-transparent"}`}
            >
              {fmtTime(s.start_utc, userTz)}
              {isAbroad && <DayShift iso={s.start_utc} date={date} />}
            </button>
          );
        })}
      </div>
      {slots?.length === 0 && <p className="mt-2 text-sm text-[#697386]" data-testid="slots-none">No times are open on this date. Please choose another day.</p>}

      {selected && (
        <div className="mt-6 pt-6 border-t border-[rgba(20,35,55,0.10)]" data-testid="slot-selection-summary">
          <p className="text-sm text-[#172033]">
            <span className="font-semibold">{fmtTime(selected.start_utc, userTz)}</span>, {fmtDateLong(selected.start_utc, userTz)}
          </p>
          {isAbroad && (
            <p className="mt-1 text-xs text-[#697386] flex items-center gap-1.5" data-testid="slot-dubai-time">
              <Globe size={12} /> {fmtTime(selected.start_utc, TZ)} in Dubai (GST, UTC+4)
            </p>
          )}
          <button onClick={onContinue} className="btn-primary w-full mt-5" data-testid="slot-continue-button">
            Continue <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
